/* global NGXD, NGX */

// Core lib dependencies.
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import moment from 'moment';

// Component dependencies.
import Components from './components.js';
import Actions from './actions.js';
import Reducer from './reducer.js';

// Misc dependencices.
import { getLabel } from '../../common/utils/string.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';

// Model objects.
import { Entity } from './model.js';

/**
 * <pre>
 * Goal.
 *
 * This app is responsible for rendering and handling all functionality relating to Goals.
 *
 * Summary of responsibilities:
 *
 *  - registers itself and any dependencies with NGXD
 *  - dispatches Redux action to register initial state for each instance of Goal React component
 *  - renders React component to page
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:Goal
 * @namespace
 */
const App = {
  init: function () {
    // Register ourselves with NGXD.
    // JSDoc FYI - all scoped methods of this closure have explicit @memberof tags, because JSDoc is not good at understanding closures!
    NGXD.registerApp(
      Config.publicName,
      Config.stateKey,
      Reducer.rootReducer,
      (function () {
        'use strict';

        // Redux _store.
        var _store;

        // The generated app config.
        var appConfig = {};

        // The service container.
        var _serviceContainer;

        /**
         * Init.
         *
         * @memberof module:Goal.App
         *
         * @param store				{object} The Redux store.
         * @param moduleConfigs		{array} Array of module config objects.
         * @param serviceContainer 	{Common.Services.ServiceContainer} Service container.
         */
        const init = (store, moduleConfigs, serviceContainer) => {
          // Store injected params.
          _store = store;
          _serviceContainer = serviceContainer;

          // Loop over each modules config.
          for (const moduleId in moduleConfigs) {
            // Get the module config.
            const moduleConfig = moduleConfigs[moduleId];

            // Extract and generate config to send into the React component as props, based on module config received.
            const id = moduleId;
            const module = moduleConfig.module;
            const metricDef = moduleConfig.metricDef;
            const metricId = metricDef.id;
            const metricConfig = metricDef.config ? metricDef.config : {};
            const metricDisplayConfig = metricDef.displayConfig ? metricDef.displayConfig : {};

            const type = generateType(metricDef);
            const strategies = generateStrategies(metricDef);
            const dates = generateDates(metricDef);

            const targets = generateTargets(metricConfig, type, dates);
            const milestones = generateMilestones(metricConfig, strategies);
            const templateOpts = generateTemplateOpts(module);
            const labels = generateLabels(module, metricDisplayConfig);
            const formatting = generateFormatting(module);
            const moduleItem = generateModuleItem(module);

            // Store all the generated config data.
            const moduleAppConfig = {
              id:           id,
              module:       module,
              metricId:     metricId,
              type:         type,
              strategies:   strategies,
              dates:        dates,
              targets:      targets,
              milestones:   milestones,
              templateOpts: templateOpts,
              labels:       labels,
              formatting:   formatting,
              moduleItem:   moduleItem,
            };

            // Store this instances config for future reference.
            appConfig[moduleId] = moduleAppConfig;

            // Set initial state for this instance.
            dispatch(
              Actions.registerInstance(id, {
                milestones: moduleAppConfig.milestones,
                intervals:  { progress: Config.intervals.progress },
              })
            );

            // Get global config service.
            const globalConfig = _serviceContainer.getService('config');

            //  we are only passing assetUrl and asstUgcUrl at this point as context.  Might need to add more later.
            const config = {
              assetUrl:    globalConfig.get('asset.url'),
              assetUgcUrl: globalConfig.get('asset.ugcUrl'),
            };

            // Store is passed to all child smart/container components via the ReactRedux 'Provider' convenience method.
            createRoot(document.getElementById(moduleAppConfig.id)).render(
              <Provider store={_store}>
                <Components.Goal
                  key={moduleAppConfig.id}
                  id={moduleAppConfig.id}
                  metricId={moduleAppConfig.metricId}
                  type={moduleAppConfig.type}
                  strategies={moduleAppConfig.strategies}
                  dates={moduleAppConfig.dates}
                  targets={moduleAppConfig.targets}
                  milestones={moduleAppConfig.milestones}
                  templateOpts={moduleAppConfig.templateOpts}
                  labels={moduleAppConfig.labels}
                  formatting={moduleAppConfig.formatting}
                  moduleItem={moduleAppConfig.moduleItem}
                  config={config}
                  triggerEvent={(event, params) => NGX.Analytics.trigger(event, params)}
                />
              </Provider>
            );
          }
        };

        /**
         * Generate type object from metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param metricDef {object} The metric def object.
         * @return {string} The type.
         */
        const generateType = (metricDef) => {
          const type = metricDef.type;

          return type;
        };

        /**
         * Generate strategies object from metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param metricDef {object} The metric def object.
         * @return {module:Goal.Model.Entity.Strategies} The generated object.
         */
        const generateStrategies = (metricDef) => {
          const strategies = new Entity.Strategies(
            // Milestone strategy.
            metricDef.strategy,

            // Overflow/excess strategy.
            metricDef.overflow,

            // Fraud prevention strategy
            metricDef.fraud
          );

          return strategies;
        };

        /**
         * Generate dates object from metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param metricDef {object} The metric def object.
         * @return {module:Goal.Model.Entity.Dates} The generated object.
         */
        const generateDates = (metricDef) => {
          const start = moment(metricDef.validFrom);
          const end = moment(metricDef.validTo);

          const dates = new Entity.Dates(
            // Start/end timestamps.
            start.valueOf(),
            end.valueOf(),

            // Human readable versions.
            start.format(),
            end.format()
          );

          return dates;
        };

        /**
         * Generate goal targets object from metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param metricConfig {object} The metric def config object.
         * @param type {string} The metric type.
         * @param dates {module:Goal.Model.Entity.Dates} The metric dates.
         * @return {module:Goal.Model.Entity.Targets} The generated object.
         */
        const generateTargets = (metricConfig, type, dates) => {
          let numeric = null;
          if (type == Enums.GOAL_TYPE_NUMBER || type == Enums.GOAL_TYPE_DATENUMBER) {
            numeric = metricConfig.goal ? metricConfig.goal : 0;
          }

          let date = null;
          if (type == Enums.GOAL_TYPE_DATE || type == Enums.GOAL_TYPE_DATENUMBER) {
            date = dates.end;
          }

          const targets = new Entity.Targets(numeric, date);

          return targets;
        };

        /**
         * Generate milestones object from metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param metricConfig {object} The metric def config object.
         * @param strategies {module:Goal.Model.Entity.Strategies} The strategies object.
         * @return {(Array<module:Goal.Model.Entity.Milestone>|Array<module:Goal.Model.Entity.GatedMilestone>)} The generated array.
         */
        const generateMilestones = (metricConfig, strategies) => {
          let milestones;

          if (
            strategies.milestone == Enums.GOAL_MILESTONE_STRATEGY_MILESTONE ||
            strategies.milestone == Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE
          ) {
            milestones = [];

            if (metricConfig.milestones) {
              for (const milestoneKey in metricConfig.milestones) {
                const milestoneDef = metricConfig.milestones[milestoneKey];

                let milestone;
                switch (strategies.milestone) {
                  case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:
                    milestone = new Entity.Milestone(milestoneDef.id, milestoneDef.goal, milestoneDef.label);

                    break;

                  case Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE:
                    milestone = new Entity.GatedMilestone(
                      milestoneDef.id,
                      milestoneDef.goal,
                      milestoneDef.label,

                      // Start/end timestamps.
                      milestoneDef.startDate,
                      milestoneDef.endDate,

                      // Human readable versions.
                      moment(milestoneDef.startDate).format(),
                      moment(milestoneDef.endDate).format()
                    );

                    break;
                }

                milestones.push(milestone);
              }
            }
          }

          return milestones;
        };

        /**
         * Generate template options object from module.
         *
         * @memberof module:Goal.App
         *
         * @param module {object} The module.
         * @return {module:Goal.Model.Entity.TemplateOptions} The generated object.
         */
        const generateTemplateOpts = (module) => {
          const templateParts = module.data.template.split(':');
          const viewName = templateParts[2] ? templateParts[2] : module.type;
          const styleParts = templateParts[3].split('.');
          const moduleStyle = styleParts[0] ? styleParts[0] : 'Horizontal';

          // Support different sizing of milestones (equal, proportional, highlight)
          const milestoneSize = 'equal';

          const templateOpts = new Entity.TemplateOptions(
            // Template type.
            viewName,

            // Orientation.
            moduleStyle,

            // Milestone size.
            milestoneSize
          );

          return templateOpts;
        };

        /**
         * Generate labels object from module and metric definition.
         *
         * @memberof module:Goal.App
         *
         * @param module {object} The module.
         * @param metricDisplayConfig {object} The metric def display config object.
         * @return {object} The generated object.
         */
        const generateLabels = (module, metricDisplayConfig) => {
          const labels = {
            // Heading related.
            heading:     module.data.heading,
            byline:      module.data.byline,
            description: module.data.description,

            // Goal related.
            goal: {
              notStarted: metricDisplayConfig.goalNotStartedMessage,
              open:       metricDisplayConfig.goalOpenMessage,
              reached:    metricDisplayConfig.goalReachedMessage,
              notReached: metricDisplayConfig.goalNotReachedMessage,
              value:      metricDisplayConfig.participateStatisticsLabel
                ? metricDisplayConfig.participateStatisticsLabel
                : getLabel('goal.summary.total'),
              valueOf:        getLabel('goal.summary.of'),
              valueDateEnds:  getLabel('goal.summary.dateEnds'),
              valueDateEnded: getLabel('goal.summary.dateEnded'),
            },

            // Milestone related.
            milestone: {
              reached:    metricDisplayConfig.milestoneReachedMessage,
              notReached: metricDisplayConfig.milestoneNotReachedMessage,
            },

            // Button related.
            button: {
              participate: metricDisplayConfig.participateLabel
                ? metricDisplayConfig.participateLabel
                : getLabel('goal.button.participate'),
              alreadyParticipated: metricDisplayConfig.alreadyParticipatedLabel
                ? metricDisplayConfig.alreadyParticipatedLabel
                : getLabel('goal.button.alreadyParticipated'),
            },

            // Confirm related.
            thanksForParticipating: metricDisplayConfig.thanksForParticipatingMessage,
          };

          return labels;
        };

        /**
         * Generate formatting object from module.
         *
         * @memberof module:Goal.App
         *
         * @param module {object} The module.
         * @return {module:Goal.Model.Entity.Formatting} The generated object.
         */
        const generateFormatting = (module) => {
          const decimalPlaces = module.data.decimalPlaces
            ? parseInt(module.data.decimalPlaces)
            : Config.formatting.decimalPlaces;
          const displayStatStrategy = module.data.participateStatisticsStrategy
            ? module.data.participateStatisticsStrategy
            : Enums.GOAL_STAT_DISPLAY_STRATEGY_NONE;

          const formatting = new Entity.Formatting(decimalPlaces, displayStatStrategy);

          return formatting;
        };

        /**
         * Generate item object from module.
         *
         * @memberof module:Goal.App
         *
         * @param module {object} The module.
         * @return {object} The generated object.
         */
        const generateModuleItem = (module) => {
          const item = {
            content: {
              heading:              module.data.heading,
              byline:               module.data.byline,
              description:          module.data.description,
              link:                 module.data.link,
              linkText:             module.data.linkText,
              featureImageAssetRef: module.data.featureImageAssetRef,
            },
          };

          return item;
        };

        /**
         * Convenience method for calling _store dispatch method. Checks _store exists first.
         *
         * @memberof module:Goal.App
         *
         * @param action {object} The Redux action.
         */
        const dispatch = (action) => {
          if (_store) {
            _store.dispatch(action);
          }
        };

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        // API methods.
        const api = {
          // Return app config.
          getConfig: () => {
            return appConfig;
          },
        };

        // Expose scoped properties.
        return {
          init: init,
          api:  api,
        };
      })(),
      // Array of additional reducer dependencies.
      []
    );
  },
};

export default App;
